import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useLeverageSelect } from '@pages/Traders-Room/Trading-Accounts/NewTradingAccount/Demo/hooks'

import { useWindowResize } from '@utils/domUtils'
import { isOne } from '@utils/validations'

import { CreateTradingAccountCampaignFormProps } from '../CreateTradingAccountCampaign.types'
import { usePlatformData } from './usePlatformData'
import { useTradingAccountCreationOptionsFetch } from './useTradingAccountCreationOptionsFetch'

export const usePreselectEffect = (
  props: CreateTradingAccountCampaignFormProps,
  platformDataProps: ReturnType<typeof usePlatformData>,
  tradingAccountCreationOptionsFetchProps: ReturnType<typeof useTradingAccountCreationOptionsFetch>
) => {
  const { values, setFieldValue, validateForm, setTouched } = props
  const { platformsAllowToOpenAccount, accountTypes } = platformDataProps
  const { campaignCurrencies } = tradingAccountCreationOptionsFetchProps

  const isMobile = useWindowResize()
  const location = useLocation()

  const [isPlatformFormDisabled, setIsPlatformFormDisabled] = useState(false)

  const { leverageType } = useLeverageSelect(values.platformType)

  const { platformType, introducingBroker } = values
  const platformTypeId = platformType?.id
  const introducingBrokerId = introducingBroker?.id
  const accountIbId = props.account?.introducingBroker?.id

  useEffect(() => {
    const platformsCount = platformsAllowToOpenAccount.length
    const shouldDisable = platformsCount <= 1
    setIsPlatformFormDisabled(shouldDisable)

    if (platformsCount === 1) {
      const singlePlatform = platformsAllowToOpenAccount[0]
      if (platformTypeId !== singlePlatform.platform.id) {
        setFieldValue('platformType', singlePlatform.platform)
        props.onChangePlatform(singlePlatform.platform.id, introducingBrokerId ?? accountIbId)
      }
    }
  }, [
    platformsAllowToOpenAccount,
    platformTypeId,
    introducingBrokerId,
    accountIbId,
    props.onChangePlatform,
    setFieldValue,
  ])

  useEffect(() => {
    selectFirstCurrency()
  }, [campaignCurrencies])

  const selectFirstCurrency = () => {
    const currencies = values.campaignId
      ? campaignCurrencies
      : props.currencies.map((currency) => currency.id)

    if (!currencies.length) return
    if (values.currencyId && currencies.includes(values.currencyId)) return

    setFieldValue('currencyId', isOne(currencies.length) ? currencies[0] : undefined)
  }

  useEffect(() => {
    values?.platformType?.id && selectFirstCurrency()
  }, [props.currencies, values?.platformType?.id])

  useEffect(() => {
    values?.currencyId && selectFirstAccountType()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.currencyId, accountTypes])

  const selectFirstAccountType = () => {
    if (accountTypes.some((accountType) => accountType.id === values.accountType?.id)) return
    setFieldValue('accountType', isOne(accountTypes.length) ? accountTypes[0] : undefined)
  }

  useEffect(() => {
    const updateLeverage = () => {
      if (values?.accountType?.id && values.platformType?.id && !values.leverageType?.id) {
        setFieldValue('leverageType', leverageType)
      }
    }
    updateLeverage()
  }, [
    values?.accountType?.id,
    leverageType,
    setFieldValue,
    values.platformType?.id,
    values.leverageType?.id,
  ])

  useEffect(() => {
    restorePreviousValues()
  }, [location.state?.selectedPlatformType])

  const restorePreviousValues = () => {
    if (!isMobile || !location.state?.previousValues || !location.state?.selectedPlatformType) {
      return
    }
    if (values.platformType?.id !== location.state.selectedPlatformType.id) {
      setFieldValue('platformType', location.state.selectedPlatformType, false)
    }
    if (location.state.previousValues.platformType?.id === location.state.selectedPlatformType.id) {
      if (values.currencyId !== location.state.previousValues.currencyId) {
        setFieldValue('currencyId', location.state.previousValues.currencyId, false)
      }
      if (values.leverageType?.id !== location.state.previousValues.leverageType?.id) {
        setFieldValue('leverageType', location.state.previousValues.leverageType, false)
      }
      if (!values.accountType) {
        setFieldValue('accountType', location.state.previousValues.accountType, false)
      }
    }
    setTouched(
      { platformType: true, currencyId: true, leverageType: true, accountType: true },
      true
    )
  }

  useEffect(() => {
    isMobile && validateForm()
  }, [
    values.platformType,
    values.currencyId,
    values.leverageType,
    values.accountType,
    isMobile,
    validateForm,
  ])

  useEffect(() => {
    validateForm()
  }, [values.referralOptionId, validateForm])

  return { isPlatformFormDisabled }
}
